/**
 * Lire la composition d'un titre de produit.
 *
 * ## Pourquoi ces fonctions vivent dans leur propre fichier
 *
 * Même raison que `texteAttribut.js` et `ficheProduit.js`, et c'est la **troisième** fois dans
 * ce composant : dans un `.jsx`, rien n'est vérifiable hors ligne — le projet teste au script
 * Node, et Node ne lit pas le JSX. Or ce qui suit est une lecture de texte à frontières de
 * mots, c'est-à-dire exactement le genre de logique qu'on croit juste en la relisant.
 *
 * ## ⚠️ Ce fichier ne compose jamais un titre
 *
 * Il dit ce qu'un titre **porte** — pour que l'écran puisse montrer, brique par brique, ce qui
 * y est déjà et ce qui manque. Le titre, lui, est toujours calculé par le serveur
 * (`services/feedOptimizerGabaritTitre.js`), et c'est ce qui empêche l'aperçu de promettre un
 * résultat qu'on n'obtiendrait pas.
 *
 * ⚠️ `BRIQUES_TITRE` est **recopiée** de `BRIQUES` côté serveur, qui fait foi. Même parti que
 * `memeMarque`, dupliquée elle aussi : l'écran ne peut pas importer un module du backend, et
 * proposer ici une brique que le serveur ignore afficherait un réglage sans effet.
 */

/**
 * Les briques dont un titre peut être composé, et leur libellé.
 *
 * ⚠️ **Recopiées du serveur, et elles doivent le rester** — `BRIQUES` dans
 * `services/feedOptimizerGabaritTitre.js` fait foi. Même parti que `memeMarque`, dupliquée
 * plus haut pour la même raison : l'écran ne peut pas importer un module du backend, et
 * proposer ici une brique que le serveur ignore afficherait un réglage sans effet.
 *
 * L'ordre de cette liste n'est que celui du choix proposé ; l'ordre **du titre** est celui
 * que l'utilisateur compose, et c'est tout l'objet du réglage.
 */
const BRIQUES_TITRE = [
  { cle: 'type', libelle: 'Nom du produit' },
  { cle: 'details', libelle: 'Détails' },
  { cle: 'matiere', libelle: 'Matière' },
  { cle: 'couleur', libelle: 'Couleur' },
  { cle: 'motif', libelle: 'Motif' },
  { cle: 'genre', libelle: 'Genre' },
  { cle: 'taille', libelle: 'Taille' },
  { cle: 'coupe', libelle: 'Coupe' },
  { cle: 'marque', libelle: 'Marque' }
];

/**
 * Un gabarit est-il posé ? Si oui, le réglage de position de la marque s'efface.
 *
 * ⚠️ **La condition porte sur l'existence du gabarit, pas sur sa brique « Marque »** — et
 * c'était un vrai défaut : un gabarit qui ne porte pas la brique vaut « sans marque », et
 * écrase donc le réglage. Les trois boutons restaient affichés et **ne faisaient plus rien**.
 *
 * Un gabarit décrit le titre en entier : la marque y est, ou elle n'y est pas. Il n'y a plus
 * de place pour un second réglage de position — seule l'orthographe survit, parce qu'elle
 * n'est pas une question de place.
 */
const gabaritPose = (gabarit) => Array.isArray(gabarit) && gabarit.length > 0;

/**
 * Un texte, ramené à sa forme comparable : minuscules, sans accents ni ponctuation.
 *
 * ⚠️ Jumelle de `aplatir` dans `services/feedOptimizerGabaritTitre.js`, et les deux doivent
 * rester d'accord — même parti que `memeMarque`, dupliquée plus haut pour la même raison.
 * Elle ne sert ici qu'à **dire** si une valeur figure dans le titre, jamais à composer : le
 * titre, lui, est toujours calculé par le serveur.
 */
const aplatirTexte = (texte) => String(texte ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * Cette valeur figure-t-elle déjà dans ce titre ?
 *
 * Frontière de mot, pour la raison que tout ce code connaît : « ample » ne doit pas se
 * reconnaître dans « exemple ».
 */
/** Ce que chaque état dit, au survol de la pastille. */
const MOTS_ETAT_BRIQUE = {
  presente: 'Déjà dans le titre',
  manquante: 'Absent du titre — l’ajouter l’enrichirait',
  absente: 'Ce produit ne renseigne pas cet élément'
};

/**
 * À quelle position ce morceau figure-t-il dans le titre ? `-1` s'il n'y est pas.
 *
 * ## ⚠️ Un morceau peut être dans le titre sans y être d'un seul tenant
 *
 * C'est le défaut que cette fonction existe pour réparer, et il faisait disparaître le mot le
 * plus important du titre. Le composant valait « Doudoune boutonnée » et le modèle avait écrit
 * « BZB, Doudoune **col duveteux** boutonnée Marron Femme » : il a intercalé les détails au
 * milieu du nom du produit. Une recherche de suite contiguë ne trouvait rien, la brique
 * « Nom du produit » passait dans « à ajouter », et le premier déplacement d'une autre brique
 * figeait un gabarit sans elle — « Doudoune » tombait, sans un mot.
 *
 * Mesuré sur les suggestions du parc : 133 morceaux d'un seul tenant, **2 dispersés** — et les
 * deux perdaient l'essentiel (le nom du produit dans un cas, les détails dans l'autre).
 *
 * ## Les deux passes, dans cet ordre
 *
 * 1. **La suite contiguë**, qui est le cas sûr et le plus fréquent : son rang est exact.
 * 2. **Tous les mots présents**, le rang étant celui du premier mot du morceau. C'est la règle
 *    du ciblage large déjà écrite ailleurs dans le projet (`lib/blocageMotsClesNegatifs.js`) —
 *    et c'est bien un morceau du titre, simplement entrecoupé par un autre.
 *
 * ⚠️ **Exiger TOUS les mots, jamais un seul.** Un morceau reconnu sur un mot commun — « et »,
 * « en » — serait classé présent sur n'importe quel titre, et le gabarit se remplirait de
 * briques vides. C'est le mot rare du morceau qui fait le travail, et il ne peut le faire que
 * si l'on exige l'ensemble.
 */
const rangDansLeTitre = (titre, valeur) => {
  const plat = aplatirTexte(titre);
  const cherche = aplatirTexte(valeur);
  if (!plat || !cherche) return -1;

  const contigu = ` ${plat} `.indexOf(` ${cherche} `);
  if (contigu >= 0) return contigu;

  const mots = cherche.split(' ').filter(Boolean);
  /*
   * Un morceau d'un seul mot sort ici, et c'est un court-circuit, **pas** un garde-fou : pour
   * un mot unique les deux passes font exactement le même test, la première l'ayant déjà
   * refusé. C'est la frontière de mot de la passe 1 qui protège « slim » de « slimane », et
   * elle protège aussi bien ici — vérifié en retirant cette ligne, aucun contrôle ne bouge.
   */
  if (mots.length < 2) return -1;
  if (!mots.every(mot => ` ${plat} `.includes(` ${mot} `))) return -1;

  // Le rang du premier mot du morceau : c'est là que le lecteur le voit commencer.
  return ` ${plat} `.indexOf(` ${mots[0]} `);
};

const dejaDansLeTitre = (titre, valeur) => rangDansLeTitre(titre, valeur) >= 0;

/**
 * Le gabarit que le titre actuel porte déjà, dans son ordre.
 *
 * ## ⚠️ Pourquoi un gabarit vide est un mauvais point de départ
 *
 * Le panneau annonçait « Aucun élément : le titre reste celui de l'IA ». C'était faux : un
 * titre **est** composé d'éléments — « Short brodé Ecru Femme taille XS en Lin et Coton,
 * broderies florales, BZB » en porte sept. Ne rien montrer obligeait à les retrouver soi-même
 * avant de pouvoir en ajouter un.
 *
 * On lit donc la composition réelle du titre : chaque brique dont la valeur y figure, triée
 * par sa position dans la phrase. L'écran part de ce qui est, pas de rien.
 *
 * ⚠️ **Ce gabarit est un état d'affichage, pas une composition.** Il n'est jamais appliqué de
 * lui-même : `gabaritTouche` l'exige, et c'est ce qui empêche d'écraser un titre que personne
 * n'a demandé à changer. Le titre, lui, reste toujours calculé par le serveur.
 */
const gabaritDepuisLeTitre = (titre, composants, marque) => {
  // ⚠️ Un objet, pas une valeur vraie : une chaîne passerait le test et seule la marque —
  // qui vient du troisième argument — serait déduite.
  if (!titre || !composants || typeof composants !== 'object') return [];

  const rangs = [];

  for (const { cle } of BRIQUES_TITRE) {
    const brute = cle === 'marque' ? marque : composants[cle];
    const valeur = Array.isArray(brute) ? brute.filter(Boolean).join(' ') : brute;
    if (!valeur) continue;

    const rang = rangDansLeTitre(titre, valeur);
    if (rang < 0) continue;

    rangs.push({ cle, rang });
  }

  return rangs.sort((a, b) => a.rang - b.rang).map(r => r.cle);
};

const libelleBrique = (cle) => BRIQUES_TITRE.find(b => b.cle === cle)?.libelle || cle;

export {
  BRIQUES_TITRE,
  libelleBrique,
  MOTS_ETAT_BRIQUE,
  gabaritPose,
  aplatirTexte,
  dejaDansLeTitre,
  rangDansLeTitre,
  gabaritDepuisLeTitre
};
